"use client"

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react"
import { isCapacitorNative } from "@/lib/capacitor"
import { lockModalViewport, unlockModalViewport } from "@/lib/capacitor/modal-viewport-lock"

type NativeStatusBarContextValue = {
  modalOverlayDepth: number
  notifyModalOpenChange: (open: boolean) => void
}

const NativeStatusBarContext = createContext<NativeStatusBarContextValue>({
  modalOverlayDepth: 0,
  notifyModalOpenChange: () => {},
})

function getScrollLockTarget(): HTMLElement | null {
  return document.querySelector("main.cap-mobile-main-pad")
}

function queryRadixOpenModalPresent(): boolean {
  if (typeof document === "undefined") return false
  return Boolean(
    document.querySelector(
      '[data-app-modal="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]'
    )
  )
}

export function NativeStatusBarProvider({ children }: { children: ReactNode }) {
  const [modalOverlayDepth, setModalOverlayDepth] = useState(0)
  const [radixModalOpen, setRadixModalOpen] = useState(false)
  const lockedScrollTopRef = useRef(0)

  const notifyModalOpenChange = useCallback((open: boolean) => {
    setModalOverlayDepth((d) => (open ? d + 1 : Math.max(0, d - 1)))
  }, [])

  useEffect(() => {
    if (!isCapacitorNative()) return undefined

    const syncRadix = () => setRadixModalOpen(queryRadixOpenModalPresent())
    syncRadix()
    const mo = new MutationObserver(syncRadix)
    mo.observe(document.body, {
      subtree: true,
      attributes: true,
      attributeFilter: ["data-state"],
    })
    return () => mo.disconnect()
  }, [])

  const modalOverlayOpen = modalOverlayDepth > 0 || radixModalOpen

  useEffect(() => {
    const main = getScrollLockTarget()

    if (modalOverlayOpen) {
      document.documentElement.setAttribute("data-modal-overlay-open", "")
      if (isCapacitorNative()) lockModalViewport()
      if (main) {
        lockedScrollTopRef.current = main.scrollTop
      }
    } else {
      document.documentElement.removeAttribute("data-modal-overlay-open")
      if (isCapacitorNative()) unlockModalViewport()
      if (main) {
        main.scrollTop = lockedScrollTopRef.current
      }
    }
  }, [modalOverlayOpen, modalOverlayDepth, radixModalOpen])

  const value = useMemo(
    () => ({ modalOverlayDepth, notifyModalOpenChange }),
    [modalOverlayDepth, notifyModalOpenChange]
  )

  return (
    <NativeStatusBarContext.Provider value={value}>{children}</NativeStatusBarContext.Provider>
  )
}

export function useNativeStatusBar() {
  return useContext(NativeStatusBarContext)
}
